import { KonvaNodeEvents, TextPath, Path as PathKonva, Line as LineKonva } from "react-konva";
import { useTheme } from "@shared/EDS/contexts/useTheme";
import Konva from "konva";
import { useMemo, useRef } from "react";
import { TRACK_LINE_WIDTH } from "../core/styles";
import { useEPDStyle } from "../contexts/EPDStyle/useEPDStyle";
import { useFonts } from "@shared/common/hooks/useFonts";
import { CutLineStrategy } from "@opstel/geo/GetLinePartsStrategies/CutLineStrategy";
import { Path } from "@opstel/geo/Path";
import { LinePartHelper } from "@opstel/geo/helpers/LinePartHelper";
import { PointHelper } from "@opstel/geo/helpers/PointHelper";
import { last } from "@shared/common/types/Array";
import { Point } from "@opstel/geo/Point";
import { LinePart } from "@opstel/geo/types";
import { SCALING_VECTOR } from "../core/layoutConstants";

type TrackLabelProps = {
  readonly onPointerOut: KonvaNodeEvents["onPointerOut"];
  readonly onPointerOver: (e: string) => void;
  readonly text: string;
  readonly trackPoints: Point[];
  readonly trackCode: string;
  readonly shuntingUnitLineParts?: LinePart[]; // this is needed to detect if a shunting unit is above the label and the label thus should be placed above the shunting unit.
};

export const TrackLabel = ({
  onPointerOut,
  onPointerOver,
  text,
  trackPoints,
  trackCode,
  shuntingUnitLineParts = [],
}: TrackLabelProps) => {
  const textPathRef = useRef<Konva.TextPath>(null);

  const { style } = useEPDStyle();
  const { theme } = useTheme();
  const fontLoaded = useFonts(theme.typography.family);

  const handlePointerOver = () => onPointerOver(trackCode);

  // The svg path for the side of the label. This is needed because the Konva line does not support rounded corners.
  const pathSide = "M-.1-.9C-.1-.95-.05-1 0-1L.05-1 .05 1 0 1C-.05 1-.1.95-.1.9Z";

  const textPoints = useMemo(() => {
    const drawPoints = trackPoints.map((coord) => coord.scale(SCALING_VECTOR));
    return isUpsideDown(drawPoints) ? drawPoints.toReversed() : drawPoints;
  }, [trackPoints]);

  const pointsBg = useMemo(() => {
    if (fontLoaded && textPathRef?.current) {
      const width = textPathRef.current.getTextWidth();

      const p = new Path(textPoints);
      const s = new CutLineStrategy([
        { type: "*" },
        { type: "px", value: width + 4 * TRACK_LINE_WIDTH, context: { id: 1 } },
        { type: "*" },
      ]);
      const { path, linePart } = p.getPathSegments(s, (c) => c?.id === 1)[0];

      const intersects = LinePartHelper.areIntersecting([linePart], shuntingUnitLineParts);

      if (intersects) {
        const angle = path.rotationAngle + 90;
        return path.moveInDirection(0 - TRACK_LINE_WIDTH * 19, angle).points;
      }

      return path.points;
    }
  }, [fontLoaded, textPoints, shuntingUnitLineParts]);

  const pointerEvents = {
    onPointerOut,
    onPointerOver: handlePointerOver,
  };

  return (
    <>
      {pointsBg && (
        <>
          <PathKonva
            data={pathSide}
            x={pointsBg[0].x}
            y={pointsBg[0].y}
            fill={style.infraItems.SetupTrack.backgroundColor}
            scale={{ x: TRACK_LINE_WIDTH * 9, y: TRACK_LINE_WIDTH * 9 }}
            rotation={PointHelper.getRotation(pointsBg[0], pointsBg[1])}
            {...pointerEvents}
          />
          <LineKonva
            points={PointHelper.toNumberArray(pointsBg)}
            stroke={style.infraItems.SetupTrack.backgroundColor}
            strokeWidth={18 * TRACK_LINE_WIDTH}
            lineJoin="round"
            {...pointerEvents}
          />
          <PathKonva
            data={pathSide}
            x={last(pointsBg).x}
            y={last(pointsBg).y}
            fill={style.infraItems.SetupTrack.backgroundColor}
            scale={{ x: TRACK_LINE_WIDTH * 9, y: TRACK_LINE_WIDTH * 9 }}
            rotation={PointHelper.getRotation(
              pointsBg[pointsBg.length - 1],
              pointsBg[pointsBg.length - 2],
            )}
            {...pointerEvents}
          />
        </>
      )}
      {textPoints && (
        <TextPath
          ref={textPathRef}
          align="center"
          data={(pointsBg ?? textPoints).reduce<string>((p, c) => `${p} ${c.x},${c.y}`, "M")}
          fill={style.infraItems.SetupTrack.textColor}
          fontFamily={theme.typography.family}
          fontSize={theme.typography.sizeXS.value * TRACK_LINE_WIDTH}
          text={text}
          {...pointerEvents}
        />
      )}
    </>
  );
};

function isUpsideDown(points: Point[]): boolean {
  const angle = PointHelper.getRotation(points[0], last(points));
  return angle >= 90 || angle <= -90;
}
